// src/components/LeadFormSection.tsx
// Лид-форма «Остались вопросы?» для лендинга
import React, { useState, useRef } from 'react';

const PUBLIC_KEY = 'lf_c01b486fe73da9a7329202ae';
const API_URL = 'https://hrlama.ru/api/sales/public/lead-form';

const LeadFormSection: React.FC = () => {
  const formRef = useRef<HTMLFormElement>(null);
  const [msg, setMsg] = useState<{ type: 'success' | 'error' | null; text: string }>({ type: null, text: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formRef.current) return;

    const form = formRef.current;
    const formData = new FormData(form);
    const data: Record<string, string> = {};
    formData.forEach((v, k) => { data[k] = String(v); });

    // Валидация
    const invalid = form.querySelectorAll<HTMLInputElement>('.hlf-input[required]');
    let hasInvalid = false;
    invalid.forEach((inp) => {
      if (!inp.value.trim()) {
        inp.style.borderColor = '#ef4444';
        inp.style.boxShadow = '0 0 0 4px rgba(239,68,68,0.15)';
        hasInvalid = true;
      } else {
        inp.style.borderColor = '';
        inp.style.boxShadow = '';
      }
    });

    if (hasInvalid) {
      setMsg({ type: 'error', text: '⚠️ Заполните обязательные поля' });
      return;
    }

    setSending(true);
    try {
      const r = await fetch(`${API_URL}/${PUBLIC_KEY}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const res = await r.json();
      if (r.ok && res.ok) {
        setMsg({ type: 'success', text: '✅ ' + (res.message || 'Спасибо! Мы свяжемся с вами.') });
        setDone(true);
        if (res.redirectUrl) {
          setTimeout(() => { window.location.href = res.redirectUrl; }, 1500);
        }
      } else {
        setMsg({ type: 'error', text: '⚠️ ' + (res.error || 'Ошибка отправки') });
      }
    } catch {
      setMsg({ type: 'error', text: '⚠️ Ошибка сети. Попробуйте позже.' });
    } finally {
      setSending(false);
    }
  };

  const handleInput = (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const t = e.currentTarget;
    t.style.borderColor = '';
    t.style.boxShadow = '';
  };

  return (
    <section className="hlf-section">
      <style>{`
        .hlf-section { padding: 0 20px; margin: 60px 0 0; background: transparent; position: relative; }
        .hlf-wrapper { position: relative; max-width: 520px; margin: 0 auto; padding: 24px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif; box-sizing: border-box; overflow: hidden; border-radius: 28px; background: linear-gradient(135deg, #2575fc12 0%, #6a11cb18 100%); isolation: isolate; }
        .hlf-wrapper * { box-sizing: border-box; }
        .hlf-bg-blob { position: absolute; border-radius: 50%; filter: blur(60px); opacity: 0.45; pointer-events: none; z-index: 0; animation: hlfFloat 12s ease-in-out infinite; }
        .hlf-blob-1 { width: 280px; height: 280px; background: #2575fc; top: -100px; left: -80px; animation-delay: 0s; }
        .hlf-blob-2 { width: 240px; height: 240px; background: #6a11cb; bottom: -80px; right: -60px; animation-delay: -4s; }
        .hlf-blob-3 { width: 180px; height: 180px; background: #ff6b9d; top: 40%; right: 20%; opacity: 0.25; animation-delay: -8s; }
        @keyframes hlfFloat { 0%,100%{transform:translate(0,0) scale(1);} 33%{transform:translate(30px,-20px) scale(1.1);} 66%{transform:translate(-20px,20px) scale(0.95);} }
        .hlf-card {
          position: relative;
          z-index: 1;
          padding: 36px 32px;
          border-radius: 32px;
          background: rgba(255,255,255,0.78);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255,255,255,0.35);
          box-shadow:
            0 30px 60px -15px rgba(106, 17, 203, 0.55),
            0 20px 40px -10px rgba(0, 0, 0, 0.18),
            0 0 120px -20px rgba(37, 117, 252, 0.6),
            0 0 200px -40px rgba(106, 17, 203, 0.55);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .hlf-card:hover {
          transform: translateY(-4px);
          box-shadow:
            0 40px 80px -15px rgba(106, 17, 203, 0.65),
            0 25px 50px -10px rgba(0, 0, 0, 0.22),
            0 0 160px -20px rgba(37, 117, 252, 0.75),
            0 0 240px -40px rgba(106, 17, 203, 0.65);
        }
        .hlf-header { text-align: center; margin-bottom: 24px; }
        .hlf-logo-wrap { display: inline-flex; align-items: center; justify-content: center; width: 72px; height: 72px; border-radius: 22px; background: linear-gradient(135deg, #2575fc, #6a11cb); box-shadow: 0 8px 24px -4px #2575fc66; margin-bottom: 16px; padding: 12px; animation: hlfPulse 3s ease-in-out infinite; }
        @keyframes hlfPulse { 0%,100%{box-shadow:0 8px 24px -4px #2575fc66, 0 0 40px -10px #2575fc33;} 50%{box-shadow:0 12px 32px -4px #2575fc99, 0 0 60px -10px #6a11cb55;} }
        .hlf-logo { width: 100%; height: 100%; object-fit: contain; }
        .hlf-title { margin: 0 0 6px; font-size: 22px; font-weight: 800; letter-spacing: -0.4px; background: linear-gradient(135deg, #2575fc, #6a11cb); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; color: #2575fc; }
        .hlf-subtitle { margin: 0; font-size: 13px; color: #64748b; line-height: 1.5; }
        .hlf-form { display: flex; flex-direction: column; gap: 14px; }
        .hlf-field { position: relative; animation: hlfFadeUp 0.6s ease backwards; }
        @keyframes hlfFadeUp { from{opacity:0;transform:translateY(12px);} to{opacity:1;transform:translateY(0);} }
        .hlf-icon { position: absolute; left: 16px; top: 50%; transform: translateY(-50%); font-size: 18px; opacity: 0.55; pointer-events: none; transition: all 0.3s; z-index: 2; }
        .hlf-field.hlf-has-textarea .hlf-icon { top: 22px; transform: none; }
        .hlf-input { width: 100%; padding: 16px 18px 16px 48px; border: 2px solid rgba(226,232,240,0.9); border-radius: 14px; font-size: 15px; font-family: inherit; color: #1a1a2e; background: rgba(255,255,255,0.9); outline: none; transition: all 0.25s cubic-bezier(0.4,0,0.2,1); resize: vertical; }
        .hlf-input::placeholder { color: #94a3b8; }
        .hlf-input:hover { border-color: #2575fc66; transform: translateY(-1px); box-shadow: 0 4px 12px -4px #2575fc33; }
        .hlf-input:focus { border-color: #2575fc; background: #fff; transform: translateY(-2px); box-shadow: 0 0 0 4px #2575fc1a, 0 8px 24px -8px #2575fc66; }
        .hlf-submit { position: relative; width: 100%; margin-top: 6px; padding: 17px 24px; border: none; border-radius: 14px; font-size: 15px; font-weight: 700; color: #fff; cursor: pointer; font-family: inherit; background: linear-gradient(135deg, #2575fc 0%, #6a11cb 100%); background-size: 200% 200%; box-shadow: 0 12px 25px rgba(37,117,252,0.4), 0 0 40px -10px rgba(37,117,252,0.6), inset 0 1px 0 rgba(255,255,255,0.25); transition: all 0.3s cubic-bezier(0.4,0,0.2,1); display: flex; align-items: center; justify-content: center; gap: 10px; overflow: hidden; animation: hlfGradient 4s ease infinite; }
        @keyframes hlfGradient { 0%,100%{background-position:0% 50%;} 50%{background-position:100% 50%;} }
        .hlf-submit:hover { transform: translateY(-3px); box-shadow: 0 20px 40px rgba(37,117,252,0.5), 0 0 60px -10px rgba(106,17,203,0.7), inset 0 1px 0 rgba(255,255,255,0.3); }
        .hlf-submit:active { transform: translateY(-1px); }
        .hlf-submit:disabled { opacity: 0.7; cursor: not-allowed; transform: none; }
        .hlf-submit-arrow { transition: transform 0.3s; }
        .hlf-submit:hover .hlf-submit-arrow { transform: translateX(4px); }
        .hlf-submit::before { content: ''; position: absolute; top: 50%; left: 50%; width: 0; height: 0; border-radius: 50%; background: rgba(255,255,255,0.3); transform: translate(-50%,-50%); transition: width 0.5s, height 0.5s; }
        .hlf-submit:active::before { width: 400px; height: 400px; }
        .hlf-privacy { text-align: center; font-size: 11px; color: #94a3b8; line-height: 1.5; margin-top: 4px; }
        .hlf-msg { padding: 16px 20px; margin-bottom: 16px; border-radius: 14px; font-size: 14px; font-weight: 600; text-align: center; animation: hlfFadeUp 0.4s ease; }
        .hlf-msg.hlf-success { background: linear-gradient(135deg, rgba(16,185,129,0.12), rgba(5,150,105,0.08)); color: #059669; border: 1px solid rgba(16,185,129,0.25); }
        .hlf-msg.hlf-error { background: linear-gradient(135deg, rgba(239,68,68,0.1), rgba(220,38,38,0.06)); color: #dc2626; border: 1px solid rgba(239,68,68,0.25); }
        @media (max-width: 520px) {
          .hlf-section { padding: 40px 16px; }
          .hlf-wrapper { padding: 16px; border-radius: 22px; }
          .hlf-card { padding: 24px 20px; }
          .hlf-title { font-size: 19px; }
        }
      `}</style>

      <div className="hlf-wrapper">
        <div className="hlf-bg-blob hlf-blob-1" />
        <div className="hlf-bg-blob hlf-blob-2" />
        <div className="hlf-bg-blob hlf-blob-3" />

        <div className="hlf-card">
          <div className="hlf-header">
            <div className="hlf-logo-wrap">
              <img src="/images/10.png" alt="HR LAMA" className="hlf-logo" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
            </div>
            <h2 className="hlf-title">Остались вопросы?</h2>
            <p className="hlf-subtitle">Заполните форму — мы свяжемся с вами в течение 30 минут</p>
          </div>

          {msg.type && (
            <div className={`hlf-msg hlf-${msg.type}`}>{msg.text}</div>
          )}

          {!done && (
            <form ref={formRef} className="hlf-form" onSubmit={handleSubmit} noValidate>
              <div className="hlf-field" style={{ animationDelay: '0s' }}>
                <div className="hlf-icon">👤</div>
                <input type="text" name="name" placeholder="Иван Иванов" required className="hlf-input" onInput={handleInput} />
              </div>
              <div className="hlf-field" style={{ animationDelay: '0.1s' }}>
                <div className="hlf-icon">📞</div>
                <input type="tel" name="phone" placeholder="+7 999 123-45-67" className="hlf-input" onInput={handleInput} />
              </div>
              <div className="hlf-field" style={{ animationDelay: '0.2s' }}>
                <div className="hlf-icon">✉️</div>
                <input type="email" name="email" placeholder="ivan@company.ru" required className="hlf-input" onInput={handleInput} />
              </div>
              <div className="hlf-field hlf-has-textarea" style={{ animationDelay: '0.3s' }}>
                <div className="hlf-icon">💬</div>
                <textarea name="comment" placeholder="Опишите задачу" rows={3} className="hlf-input" onInput={handleInput} />
              </div>
              <button type="submit" className="hlf-submit" disabled={sending}>
                <span className="hlf-submit-text">{sending ? 'Отправка...' : 'Отправить заявку'}</span>
                <svg className="hlf-submit-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
              </button>
              <div className="hlf-privacy">🔒 Ваши данные защищены. Мы не передаём их третьим лицам.</div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default LeadFormSection;
